import { createElement as h, useState } from 'react';

/**
 * One "Edit" button for a recorded fact (A-#225, deploy 8): it opens, in place, the choices the record allows
 * (attach a document, correct, withdraw), and each choice opens the form that record already has. Nothing is
 * changed by a choice itself. Who sees the button is `may`, the same server flag that showed the inline buttons
 * before; with `may` false, or nothing to choose, nothing is drawn.
 *
 * Written with createElement, not JSX, so the tests can render it under node (web/test/record-edit.test.ts).
 * Every record that can be changed goes through this; the test refuses an inline Correct, Withdraw or Attach.
 */

export interface EditChoice {
  key: string;
  label: string;
  onPick: () => void;
}

const LINK = 'mr-3 min-h-[44px] text-sm font-semibold text-neutral-700 underline underline-offset-2';

export function RecordEdit({ may, choices, className = LINK, startOpen = false }: {
  may: boolean;
  choices: EditChoice[];
  /** the button style of the row it sits in */
  className?: string;
  /** for the tests: drawn open */
  startOpen?: boolean;
}) {
  const [open, setOpen] = useState(startOpen);
  if (!may || choices.length === 0) return null;
  if (!open) {
    return h('button', { type: 'button', className, 'aria-expanded': false, onClick: () => setOpen(true) }, 'Edit');
  }
  return h('div', { role: 'group', 'aria-label': 'Edit this record', className: 'flex flex-wrap gap-2' },
    ...choices.map((c) => h('button', {
      key: c.key, type: 'button', className, 'data-choice': c.key,
      onClick: () => { setOpen(false); c.onPick(); },
    }, c.label)),
    h('button', { key: 'cancel', type: 'button', className, onClick: () => setOpen(false) }, 'Cancel'));
}
